/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { isEmpty } from '@ember/utils';
import FacebookLeadAdsForm from 'cio/models/facebook-lead-ads-form';
import { isFacebookForm, isCustomForm } from 'cio/utils/forms';
import CustomForm from 'cio/models/custom-form';
import { prettyJSONStringify } from 'cio/utils/objects';
import { next } from '@ember/runloop';

interface SendEventModalFormsEventDataEditorArgs {
  form: FacebookLeadAdsForm | CustomForm;
  data: string;
  onChange: (content: string) => unknown;
}

type FormEventDataField = { key: string; value: string; locked: boolean };

function toJson(fields: FormEventDataField[]) {
  let attrs: Record<string, string> = {};

  fields
    .filter((field) => !isEmpty(field.key))
    .forEach((field) => {
      let attr = field.key.trim();
      let value = typeof field.value === 'string' ? field.value?.trim() : field.value;

      try {
        attrs[attr] = JSON.parse(value);
      } catch {
        attrs[attr] = value;
      }
    });

  return prettyJSONStringify(attrs);
}

export default class SendEventModalFormsEventDataEditor extends Component<SendEventModalFormsEventDataEditorArgs> {
  @tracked fields: FormEventDataField[] = [];

  constructor(owner: unknown, args: SendEventModalFormsEventDataEditorArgs) {
    super(owner, args);

    this.fields = this.generateFields(this.args.data);

    next(() => {
      this.args.onChange(toJson(this.fields));
    });
  }

  @action
  onInput(fieldIdx: number, value: string) {
    if (this.fields[fieldIdx] != null) {
      this.fields[fieldIdx].value = value;
    }
    this.args.onChange(toJson(this.fields));
  }

  private generateFields(data: string) {
    let { form }: { form: CustomForm | FacebookLeadAdsForm } = this.args;

    let dataObject: Record<string, string> = {};
    if (data) {
      dataObject = JSON.parse(data);
    }

    let meta = [
      { key: 'form_id', value: form.formId, locked: true },
      { key: 'form_name', value: form.name, locked: true },
      { key: 'form_type', value: form.formType, locked: true },
    ];

    if (isFacebookForm(form)) {
      return [
        ...meta,
        { key: 'lead_id', value: '', locked: false },
        ...form.attrMaps.map((m) => ({
          key: m.facebookQuestionKey.toLowerCase(),
          value: dataObject[m.facebookQuestionKey.toLowerCase()] || '',
          locked: false,
        })),
      ];
    } else {
      return [
        ...meta,
        ...form.attrMaps.reduce((aggr, m) => {
          aggr.push({
            key: m.formFieldKey,
            value: m.cioAttr || '',
            locked: false,
          });

          // for Typeform forms add extra locked "<FIELD_ID>_label" property, to mirror a real "form_submit" event:
          if (isCustomForm(form) && form.isTypeform) {
            aggr.push({
              key: `${m.formFieldKey}_label`,
              value: m.formFieldLabel,
              locked: true,
            });
          }

          return aggr;
        }, [] as FormEventDataField[]),
      ];
    }
  }
}
